import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDarkMode } from "./hooks/useDarkMode";
import { useScrollReveal } from "./hooks/useScrollReveal";
import DarkModeToggle from "./components/DarkModeToggle";

import tofino2353 from "./img/travel/tofino/IMG_2353.JPG";
import tofino2365 from "./img/travel/tofino/IMG_2365.JPG";
import tofino2288 from "./img/travel/tofino/IMG_2288.JPG";

import peru8571 from "./img/travel/peru/IMG_8571.JPG";
import peru09309 from "./img/travel/peru/DSC09309.JPG";
import peru00228 from "./img/travel/peru/DSC00228.JPG";

import sf0433 from "./img/travel/san_francisco/IMG_0433.JPG";
import sf0459 from "./img/travel/san_francisco/IMG_0459.JPG";
import sf1928 from "./img/travel/san_francisco/IMG_1928.JPG";

import aus7756 from "./img/travel/austria/IMG_7756.JPG";
import aus7777 from "./img/travel/austria/IMG_7777.JPG";
import aus7923 from "./img/travel/austria/IMG_7923.JPG";
import aus8026 from "./img/travel/austria/IMG_8026.JPG";

import prague7503 from "./img/travel/prague/IMG_7503.JPG";
import prague7541 from "./img/travel/prague/IMG_7541.JPG";
import prague7545 from "./img/travel/prague/IMG_7545.JPG";
import prague7555 from "./img/travel/prague/IMG_7555.JPG";

const travelPhotos = [
  {
    title: "Tofino",
    location: "Tofino, BC",
    date: "Feb 18-21, 2026",
    people: "With friends",
    images: [tofino2353, tofino2365, tofino2288],
  },
  {
    title: "San Francisco",
    location: "San Francisco, CA",
    date: "Jan 31, 2026",
    people: "With friends",
    images: [sf1928, sf0433, sf0459],
  },
  {
    title: "Peru",
    location: "Peru",
    date: "Aug 2025",
    people: "With friends",
    images: [peru00228, peru8571, peru09309],
  },
  {
    title: "Austria",
    location: "Vienna, Austria",
    date: "July 2025",
    people: "Solo trip",
    images: [aus7777, aus7756, aus7923, aus8026],
  },
  {
    title: "Prague",
    location: "Prague, Czechia",
    date: "July 2025",
    people: "With Family",
    images: [prague7555, prague7503, prague7541, prague7545],
  },
];

const TravelPhotoCard = ({ photo }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const totalImages = photo.images.length;

  useEffect(() => {
    setIsLoaded(false);
  }, [currentIndex]);

  useEffect(() => {
    if (!isOpen || totalImages <= 1) {
      return;
    }

    const nextIndex = (currentIndex + 1) % totalImages;
    const prevIndex = (currentIndex - 1 + totalImages) % totalImages;

    const nextImage = new Image();
    nextImage.src = photo.images[nextIndex];

    const prevImage = new Image();
    prevImage.src = photo.images[prevIndex];
  }, [currentIndex, isOpen, photo.images, totalImages]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const preloaders = photo.images.map((src) => {
      const image = new Image();
      image.src = src;
      return image;
    });

    return () => {
      preloaders.forEach((image) => {
        image.src = "";
      });
    };
  }, [isOpen, photo.images]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalImages) % totalImages);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalImages);
  };

  const [ref, isVisible] = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden text-left card-hover transition-all duration-500 border border-gray-200 dark:border-gray-700 ${
        isVisible ? "animate-scale-in" : "opacity-0"
      }`}
    >
      <div className="relative">
        {!isLoaded && (
          <div className="absolute inset-0 bg-slate-200 dark:bg-gray-700 animate-pulse" />
        )}
        <img
          src={photo.images[currentIndex]}
          alt={`${photo.title} ${currentIndex + 1}`}
          className="w-full h-48 object-cover cursor-pointer"
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          onClick={() => setIsOpen(true)}
        />
        {totalImages > 1 && (
          <>
            <button
              type="button"
              onClick={handlePrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white dark:bg-gray-700/80 dark:hover:bg-gray-700 text-gray-900 dark:text-white rounded-full w-8 h-8 flex items-center justify-center transition-all hover:scale-110"
              aria-label="Previous photo"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white dark:bg-gray-700/80 dark:hover:bg-gray-700 text-gray-900 dark:text-white rounded-full w-8 h-8 flex items-center justify-center transition-all hover:scale-110"
              aria-label="Next photo"
            >
              ›
            </button>
          </>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-bold text-lg mb-1 dark:text-white">
          {photo.title}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          {photo.location}
        </p>
        <p className="text-gray-500 dark:text-gray-500 text-sm">{photo.date}</p>
        <p className="text-gray-500 dark:text-gray-500 text-sm">
          {photo.people}
        </p>
        {totalImages > 1 && (
          <div className="mt-3 flex items-center gap-2">
            {photo.images.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setCurrentIndex(index)}
                className={`h-2 w-2 rounded-full transition-all ${
                  index === currentIndex
                    ? "bg-gray-900 dark:bg-white"
                    : "bg-gray-300 dark:bg-gray-600"
                }`}
                aria-label={`Go to photo ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 dark:bg-black/90 flex items-center justify-center animate-fade-in"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="relative w-full max-w-5xl mx-4 animate-scale-in"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={photo.images[currentIndex]}
              alt={`${photo.title} ${currentIndex + 1}`}
              className="w-full max-h-[80vh] object-contain bg-black"
            />
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="absolute top-3 right-3 text-white text-3xl leading-none hover:scale-110 transition-transform"
              aria-label="Close"
            >
              &times;
            </button>
            {totalImages > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-900 rounded-full w-10 h-10 flex items-center justify-center transition-all hover:scale-110"
                  aria-label="Previous photo"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-900 rounded-full w-10 h-10 flex items-center justify-center transition-all hover:scale-110"
                  aria-label="Next photo"
                >
                  ›
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
                  {photo.images.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setCurrentIndex(index)}
                      className={`h-2.5 w-2.5 rounded-full ${
                        index === currentIndex ? "bg-white" : "bg-white/50"
                      }`}
                      aria-label={`Go to photo ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Travel = () => {
  const [isDark, setIsDark] = useDarkMode();

  return (
    <div className="min-h-screen relative overflow-hidden dark:bg-gray-900 dark-mode-transition">
      <DarkModeToggle isDark={isDark} setIsDark={setIsDark} />

      <div className="relative z-10 container mx-auto p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 animate-fade-in">
          <div className="text-left">
            <h1 className="text-4xl md:text-5xl font-bold mb-2 dark:text-white">
              Travel Moments
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              The following photos capture some of my favorite travel memories
              with the people I love.
            </p>
          </div>
          <Link
            to="/"
            className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 btn-hover whitespace-nowrap self-start md:self-auto"
          >
            Back to home
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {travelPhotos.map((photo, index) => (
            <TravelPhotoCard key={index} photo={photo} />
          ))}
        </div>
      </div>

      <div className="background-container"></div>
    </div>
  );
};

export default Travel;
